/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

/** 
  All of the routes for the Material Dashboard 2 React are added here,
  You can add a new route, customize the routes and delete the routes here.

  Once you add a new route on this file it will be visible automatically on
  the Sidenav.

  For adding a new route you can follow the existing routes in the routes array.
  1. The `type` key with the `collapse` value is used for a route.
  2. The `type` key with the `title` value is used for a title inside the Sidenav. 
  3. The `type` key with the `divider` value is used for a divider between Sidenav items.
  4. The `name` key is used for the name of the route on the Sidenav.
  5. The `key` key is used for the key of the route (It will help you with the key prop inside a loop).
  6. The `icon` key is used for the icon of the route on the Sidenav, you have to add a node.
  7. The `collapse` key is used for making a collapsible item on the Sidenav that has other routes
  inside (nested routes), you need to pass the nested routes inside an array as a value for the `collapse` key.
  8. The `route` key is used to store the route location which is used for the react router.
  9. The `href` key is used to store the external links location.
  10. The `title` key is only for the item with the type of `title` and its used for the title text on the Sidenav.
  10. The `component` key is used to store the component of its route.
*/

// Material Dashboard 2 React layouts

// @mui icons
import Icon from "@mui/material/Icon";
import ChangePassword from "authentication/change-password";
import ChecklistItemTable from "checklistItems";
import AddChecklistItems from "checklistItems/add";
import Dashboard from "dashboard";
import DocumentsTable from "documents";
import UploadDocument from "documents/upload";
import EventTable from "event";
import AddEvent from "event/add";
import InspectionsTable from "inspections";
import InspectionDetails from "inspections/view";
import SparePartsTable from "inventorySpareparts";
import AddSpareParts from "inventorySpareparts/add";
import RideTable from "ride";
import AddRide from "ride/add";
import AddStaff from "staff/add";
import StaffTable from "staff/index";
import VendorTable from "vendors";
import AddVendor from "vendors/add";
import WorkOrdersTable from "workOrders";
import AddWorkOrder from "workOrders/add";

export const sidebarRoutes = [
  {
    type: "collapse",
    name: "Dashboard",
    key: "dashboard",
    icon: <Icon fontSize="small">dashboard</Icon>,
    route: "/dashboard",
    component: <Dashboard />,
  },
  {
    type: "collapse",
    name: "Inspections Reports",
    key: "inspectionsReports",
    icon: <Icon fontSize="small">fact_check</Icon>,
    route: "/inspections-reports",
    component: <InspectionsTable />,
  },
  {
    type: "collapse",
    name: "Work Orders",
    key: "workOrders",
    icon: <Icon fontSize="small">build</Icon>,
    route: "/work-orders",
    component: <WorkOrdersTable />,
  },
  {
    type: "collapse",
    name: "Inventory & Parts",
    key: "inventory",
    icon: <Icon fontSize="small">inventory_2</Icon>,
    route: "/spare-parts",
    component: <SparePartsTable />,
  },
  {
    type: "collapse",
    name: "Events",
    key: "events",
    icon: <Icon fontSize="small">festival</Icon>,
    route: "/events",
    component: <EventTable />,
  },
  {
    type: "collapse",
    name: "Rides",
    key: "rides",
    icon: <Icon fontSize="small">attractions</Icon>,
    route: "/rides",
    component: <RideTable />,
  },
  {
    type: "collapse",
    name: "Checklist Items",
    key: "checklistItems",
    icon: <Icon fontSize="small">checklist</Icon>,
    route: "/checklist-items",
    component: <ChecklistItemTable />,
  },
  {
    type: "collapse",
    name: "Vendors",
    key: "vendors",
    icon: <Icon fontSize="small">peoplealt</Icon>,
    route: "/vendors",
    component: <VendorTable />,
  },
  {
    type: "collapse",
    name: "Staffs",
    key: "staffs",
    icon: <Icon fontSize="small">groups2</Icon>,
    route: "/staffs",
    component: <StaffTable />,
  },
  {
    type: "collapse",
    name: "Documents",
    key: "documents",
    icon: <Icon fontSize="small">description</Icon>,
    route: "/documents",
    component: <DocumentsTable />,
  },
];

const routes = [
  {
    name: "Add Staff",
    key: "staffs",
    route: "/staffs/add-staff",
    component: <AddStaff />,
  },
  {
    name: "Edit Staff",
    key: "staffs",
    route: "/staffs/:id",
    component: <AddStaff />,
  },
  {
    name: "Change Password",
    key: "profile",
    route: "/change-password",
    component: <ChangePassword />,
  },
  {
    name: "Add Document",
    key: "documents",
    route: "/documents/add-document",
    component: <UploadDocument />,
  },
  {
    name: "Edit Document",
    key: "documents",
    route: "/documents/:id",
    component: <UploadDocument />,
  },
  {
    name: "Add Event",
    key: "events",
    route: "/events/add-event",
    component: <AddEvent />,
  },
  {
    name: "Edit Event",
    key: "events",
    route: "/events/:id",
    component: <AddEvent />,
  },
  {
    name: "Add Ride",
    key: "rides",
    route: "/rides/add-ride",
    component: <AddRide />,
  },
  {
    name: "Edit Ride",
    key: "rides",
    route: "/rides/:id",
    component: <AddRide />,
  },
  {
    name: "Add Vendor",
    key: "vendors",
    route: "/vendors/add-vendor",
    component: <AddVendor />,
  },
  {
    name: "Edit Vendor",
    key: "vendors",
    route: "/vendors/:id",
    component: <AddVendor />,
  },
  {
    name: "Add Checklist Item",
    key: "checklistItems",
    route: "/checklist-items/add-checklist",
    component: <AddChecklistItems />,
  },
  {
    name: "Edit Checklist Item",
    key: "checklistItems",
    route: "/checklist-items/:id",
    component: <AddChecklistItems />,
  },
  {
    name: "Add SparePart",
    key: "spareParts",
    route: "/spare-parts/add-spare-part",
    component: <AddSpareParts />,
  },
  {
    name: "Edit SparePart",
    key: "spareParts",
    route: "/spare-parts/:id",
    component: <AddSpareParts />,
  },
  {
    name: "Add Work Orders",
    key: "workOrders",
    route: "/work-orders/add-work-order",
    component: <AddWorkOrder />,
  },
  {
    name: "Edit Work Orders",
    key: "workOrders",
    route: "/work-orders/:id",
    component: <AddWorkOrder />,
  },
  {
    name: "Inspection Details",
    key: "inspections",
    route: "/inspections-reports/:id",
    component: <InspectionDetails />,
  },
  // {
  //   name: "Notifications",
  //   key: "notifications",
  //   route: "/notifications",
  //   component: <NotificationList />,
  // },
];

export default routes;
